import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Cell, Legend
} from 'recharts';
import {
    MapPin, Storefront, ChartLineUp, Clock, TrendUp, TrendDown,
    CalendarBlank, Money, Users as UsersIcon, Warning, Lightbulb
} from '@phosphor-icons/react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { getLocations } from '../services/api';
import { localTodayKey, localMonthStartKey } from '../lib/dateLocal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 }).format(Number(n) || 0);
const fmtInt = (n) => new Intl.NumberFormat('es-MX').format(Number(n) || 0);

const hourLabel = (h) => `${String(h).padStart(2, '0')}:00`;
const PAYMENT_LABELS = {
    cash: 'Efectivo', credit_card: 'T. Crédito', debit_card: 'T. Débito',
    transfer: 'Transferencia', courtesy: 'Cortesía', mixed: 'Mixto'
};
const PAYMENT_COLOR = { cash: '#65C76A', credit_card: '#FB7185', debit_card: '#1F2D4B', transfer: '#A78BFA', courtesy: '#FACC15', mixed: '#94A3B8' };

const KpiTile = ({ label, value, sub, tone = 'default', icon: Icon, testId }) => (
    <div
        className={`rounded-lg border p-4 ${tone === 'dark' ? 'bg-[#1F2D4B] border-[#1F2D4B] text-white' : 'bg-white border-[#ECE3D2]'}`}
        data-testid={testId}
    >
        <div className="flex items-center justify-between mb-1.5">
            <span className={`text-[10px] uppercase tracking-wider font-semibold ${tone === 'dark' ? 'opacity-80' : 'text-[#8B8474]'}`}>{label}</span>
            {Icon && <Icon size={18} weight="duotone" className={tone === 'dark' ? 'text-[#FB7185]' : 'text-[#FB7185]'} />}
        </div>
        <p className="font-display text-2xl font-bold leading-tight">{value}</p>
        {sub && <p className={`text-xs mt-1 ${tone === 'dark' ? 'opacity-80' : 'text-[#4B5878]'}`}>{sub}</p>}
    </div>
);

const InsightCard = ({ icon: Icon, tone, title, body, testId }) => {
    const TONES = {
        good: { bg: 'bg-[#D1FAE5]', fg: 'text-[#047857]', ring: 'ring-[#65C76A]' },
        warn: { bg: 'bg-[#FEF3C7]', fg: 'text-[#92400E]', ring: 'ring-[#F59E0B]' },
        bad:  { bg: 'bg-[#FEE2E2]', fg: 'text-[#B91C1C]', ring: 'ring-[#FB7185]' },
        info: { bg: 'bg-[#E0E7FF]', fg: 'text-[#3730A3]', ring: 'ring-[#6366F1]' },
    };
    const t = TONES[tone] || TONES.info;
    return (
        <div className={`rounded-lg p-4 ${t.bg} flex gap-3 items-start ring-1 ${t.ring}/30`} data-testid={testId}>
            <Icon size={20} weight="duotone" className={`${t.fg} flex-shrink-0 mt-0.5`} />
            <div>
                <p className={`font-semibold text-sm ${t.fg}`}>{title}</p>
                <p className="text-[12.5px] text-[#1F2D4B] mt-0.5 leading-relaxed">{body}</p>
            </div>
        </div>
    );
};

const LocationDetail = ({ loc }) => {
    const kpi = loc.kpis;
    const insights = useMemo(() => {
        const out = [];
        if (kpi.coverage_pct < 50) {
            out.push({
                tone: 'warn', icon: Warning,
                title: `Solo ${kpi.coverage_pct}% de días con actividad`,
                body: `${kpi.active_days} de ${kpi.days_in_range} días generaron ventas. Considera revisar horarios, ausentismo o decidir si vale la pena seguir operando este punto.`
            });
        } else if (kpi.coverage_pct >= 90) {
            out.push({
                tone: 'good', icon: ChartLineUp,
                title: `Punto muy consistente (${kpi.coverage_pct}% de cobertura)`,
                body: `Vende prácticamente todos los días del periodo. Buen candidato para incentivos por meta o expansión de horarios.`
            });
        }
        if (loc.peak_hour && loc.valley_hour && loc.peak_hour.count > loc.valley_hour.count * 3) {
            out.push({
                tone: 'info', icon: Clock,
                title: `Pico fuerte a las ${hourLabel(loc.peak_hour.hour)}`,
                body: `La hora ${hourLabel(loc.peak_hour.hour)} concentra ${loc.peak_hour.count} ventas (${fmt(loc.peak_hour.revenue)}), mientras que ${hourLabel(loc.valley_hour.hour)} sólo ${loc.valley_hour.count}. Refuerza con una segunda promotora durante el pico.`
            });
        }
        if (loc.best_weekday && loc.worst_weekday && loc.worst_weekday.count === 0) {
            out.push({
                tone: 'warn', icon: CalendarBlank,
                title: `${loc.worst_weekday.name}: día muerto`,
                body: `Cero ventas los ${loc.worst_weekday.name.toLowerCase()}s del periodo. Evalúa cerrar ese día o usarlo para capacitación / mantenimiento.`
            });
        } else if (loc.best_weekday && loc.worst_weekday && loc.best_weekday.count > loc.worst_weekday.count * 2) {
            out.push({
                tone: 'info', icon: TrendUp,
                title: `${loc.best_weekday.name} domina la semana`,
                body: `${loc.best_weekday.name}: ${loc.best_weekday.count} ventas vs ${loc.worst_weekday.name}: ${loc.worst_weekday.count}. Asigna a tu mejor promotora los ${loc.best_weekday.name.toLowerCase()}s.`
            });
        }
        if (kpi.avg_ticket > 0 && loc.sellers.length >= 2) {
            const top = loc.sellers[0];
            const last = loc.sellers[loc.sellers.length - 1];
            if (top.revenue > last.revenue * 2) {
                out.push({
                    tone: 'info', icon: UsersIcon,
                    title: 'Brecha entre promotoras',
                    body: `${top.name} factura ${fmt(top.revenue)} mientras que ${last.name} sólo ${fmt(last.revenue)}. Considera capacitación cruzada o reasignar territorios.`
                });
            }
        }
        if (kpi.revenue_per_active_day < 500 && kpi.active_days > 5) {
            out.push({
                tone: 'bad', icon: Money,
                title: 'Punto poco rentable',
                body: `Genera apenas ${fmt(kpi.revenue_per_active_day)}/día activo. Si la renta es fija, vale la pena renegociar con la locación o intercambiar por revenue share.`
            });
        }
        return out;
    }, [loc, kpi]);

    const peakHourFill = '#FB7185';
    const regularHourFill = '#1F2D4B';
    return (
        <Card className="border-[#ECE3D2]" data-testid={`location-card-${loc.location.id}`}>
            <CardHeader>
                <CardTitle className="flex items-start justify-between gap-3">
                    <div>
                        <h3 className="font-display font-bold text-[#1F2D4B] flex items-center gap-2">
                            <Storefront size={20} weight="duotone" className="text-[#FB7185]" />
                            {loc.location.name}
                        </h3>
                        {loc.location.address && (
                            <p className="text-xs text-[#8B8474] font-normal mt-1">{loc.location.address}</p>
                        )}
                    </div>
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-[#FB7185] bg-[#FB7185]/10 px-2.5 py-1 rounded-full whitespace-nowrap">
                        {kpi.coverage_pct}% cobertura
                    </span>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
                {/* KPIs */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <KpiTile label="Ingresos" value={fmt(kpi.revenue)} sub={`${fmtInt(kpi.sales_count)} ventas`} tone="dark" icon={Money} testId={`kpi-revenue-${loc.location.id}`} />
                    <KpiTile label="Certificados" value={fmtInt(kpi.certificates_count)} sub={`Ticket prom. ${fmt(kpi.avg_ticket)}`} icon={TrendUp} />
                    <KpiTile label="Días activos" value={`${kpi.active_days}/${kpi.days_in_range}`} sub={`Ingreso/día: ${fmt(kpi.revenue_per_active_day)}`} icon={CalendarBlank} />
                    <KpiTile label="Horario observado" value={kpi.opening_hour !== null ? `${hourLabel(kpi.opening_hour)}–${hourLabel(kpi.closing_hour)}` : '—'} sub={loc.peak_hour ? `Pico: ${hourLabel(loc.peak_hour.hour)}` : 'Sin actividad'} icon={Clock} />
                </div>

                {/* Insights */}
                {insights.length > 0 && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid={`insights-${loc.location.id}`}>
                        {insights.map((i, idx) => <InsightCard key={idx} {...i} testId={`insight-${loc.location.id}-${idx}`} />)}
                    </div>
                )}

                {/* Hourly chart */}
                <div>
                    <h4 className="text-xs uppercase tracking-wider font-semibold text-[#1F2D4B] mb-2 flex items-center gap-2">
                        <Clock size={14} weight="duotone" /> Distribución por hora (ventas)
                    </h4>
                    <div className="bg-[#FAF6F0] rounded-md p-3">
                        <ResponsiveContainer width="100%" height={180}>
                            <BarChart data={loc.hourly_distribution} margin={{ left: -20 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#ECE3D2" />
                                <XAxis dataKey="hour" tick={{ fontSize: 10, fill: '#8B8474' }} tickFormatter={(h) => hourLabel(h)} interval={1} />
                                <YAxis tick={{ fontSize: 10, fill: '#8B8474' }} allowDecimals={false} />
                                <Tooltip formatter={(v) => fmtInt(v)} labelFormatter={(h) => `Hora ${hourLabel(h)}`} />
                                <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                                    {loc.hourly_distribution.map((entry, idx) => (
                                        <Cell key={idx} fill={loc.peak_hour && entry.hour === loc.peak_hour.hour ? peakHourFill : regularHourFill} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Weekday + daily */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                        <h4 className="text-xs uppercase tracking-wider font-semibold text-[#1F2D4B] mb-2">Por día de la semana</h4>
                        <div className="bg-[#FAF6F0] rounded-md p-3">
                            <ResponsiveContainer width="100%" height={180}>
                                <BarChart data={loc.weekday_distribution} margin={{ left: -20 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#ECE3D2" />
                                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#8B8474' }} interval={0} />
                                    <YAxis tick={{ fontSize: 10, fill: '#8B8474' }} allowDecimals={false} />
                                    <Tooltip formatter={(v) => fmtInt(v)} />
                                    <Bar dataKey="count" fill="#1F2D4B" radius={[3, 3, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                    <div>
                        <h4 className="text-xs uppercase tracking-wider font-semibold text-[#1F2D4B] mb-2">Tendencia diaria</h4>
                        <div className="bg-[#FAF6F0] rounded-md p-3">
                            <ResponsiveContainer width="100%" height={180}>
                                <LineChart data={loc.daily_timeline} margin={{ left: -20 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#ECE3D2" />
                                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#8B8474' }} tickFormatter={(d) => d?.slice(5)} />
                                    <YAxis tick={{ fontSize: 10, fill: '#8B8474' }} />
                                    <Tooltip formatter={(v, n) => n === 'revenue' ? fmt(v) : fmtInt(v)} />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                    <Line type="monotone" dataKey="revenue" stroke="#FB7185" strokeWidth={2} dot={false} name="Ingresos" />
                                    <Line type="monotone" dataKey="count" stroke="#1F2D4B" strokeWidth={2} dot={false} name="Ventas" />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                {/* Sellers + payment methods */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                        <h4 className="text-xs uppercase tracking-wider font-semibold text-[#1F2D4B] mb-2 flex items-center gap-2">
                            <UsersIcon size={14} weight="duotone" /> Promotoras (top)
                        </h4>
                        <div className="bg-white border border-[#ECE3D2] rounded-md divide-y divide-[#ECE3D2]">
                            {loc.sellers.slice(0, 5).map(s => (
                                <div key={s.seller_id} className="flex items-center justify-between px-3 py-2" data-testid={`seller-row-${s.seller_id}`}>
                                    <span className="text-sm font-medium text-[#1F2D4B] truncate">{s.name}</span>
                                    <span className="text-xs font-mono text-[#4B5878]">{fmtInt(s.count)} · {fmt(s.revenue)}</span>
                                </div>
                            ))}
                            {loc.sellers.length === 0 && <p className="px-3 py-3 text-xs italic text-[#8B8474]">Sin ventas en el periodo.</p>}
                        </div>
                    </div>
                    <div>
                        <h4 className="text-xs uppercase tracking-wider font-semibold text-[#1F2D4B] mb-2">Formas de pago</h4>
                        <div className="bg-white border border-[#ECE3D2] rounded-md divide-y divide-[#ECE3D2]">
                            {loc.payment_methods.map(pm => (
                                <div key={pm.method} className="flex items-center justify-between px-3 py-2">
                                    <span className="flex items-center gap-2 text-sm font-medium text-[#1F2D4B]">
                                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: PAYMENT_COLOR[pm.method] || '#94A3B8' }}></span>
                                        {PAYMENT_LABELS[pm.method] || pm.method}
                                    </span>
                                    <span className="text-xs font-mono text-[#4B5878]">{fmtInt(pm.count)} · {fmt(pm.amount)}</span>
                                </div>
                            ))}
                            {loc.payment_methods.length === 0 && <p className="px-3 py-3 text-xs italic text-[#8B8474]">—</p>}
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

const ProductivityPage = () => {
    const { isAdmin, can } = useAuth();
    const canSee = isAdmin() || can('reports.dashboard_global') || can('reports.export');
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [locations, setLocations] = useState([]);
    const [filters, setFilters] = useState({
        start_date: localMonthStartKey(),
        end_date: localTodayKey(),
        location_id: '',
    });

    useEffect(() => {
        if (!canSee) return;
        getLocations().then(({ data }) => setLocations(data || [])).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchReport = async () => {
        setLoading(true);
        try {
            const body = { ...filters, location_id: filters.location_id || null };
            const { data: resp } = await axios.post(`${API}/reports/productivity-by-location`, body);
            setData(resp);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al cargar el reporte');
        } finally { setLoading(false); }
    };

    useEffect(() => {
        if (canSee) fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!canSee) {
        return <div className="p-8 text-center text-[#4B5878]">No tienes permiso para ver este reporte.</div>;
    }

    return (
        <div className="space-y-6" data-testid="productivity-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#1F2D4B] flex items-center gap-3">
                        <ChartLineUp size={28} weight="duotone" className="text-[#FB7185]" />
                        Productividad por ubicación
                    </h1>
                    <p className="text-sm text-[#4B5878] mt-1">
                        Identifica picos, valles, días muertos y rentabilidad por sucursal — para decidir staffing, horarios y renegociaciones.
                    </p>
                </div>
            </div>

            {/* Filters */}
            <Card className="border-[#ECE3D2]">
                <CardContent className="pt-5 pb-5">
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-[#1F2D4B] font-semibold mb-1.5 block">Desde</Label>
                            <Input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} data-testid="prod-start" className="h-10 rounded-md border-[#ECE3D2]" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-[#1F2D4B] font-semibold mb-1.5 block">Hasta</Label>
                            <Input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} data-testid="prod-end" className="h-10 rounded-md border-[#ECE3D2]" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-[#1F2D4B] font-semibold mb-1.5 block">Ubicación</Label>
                            <Select value={filters.location_id || 'all'} onValueChange={(v) => setFilters({ ...filters, location_id: v === 'all' ? '' : v })}>
                                <SelectTrigger className="h-10 rounded-md border-[#ECE3D2]" data-testid="prod-location-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todas</SelectItem>
                                    {locations.map(l => (
                                        <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <Button onClick={fetchReport} disabled={loading} data-testid="prod-refresh-btn" className="h-10 bg-[#1F2D4B] hover:bg-[#0F1B3D] text-white">
                            {loading ? 'Cargando…' : 'Actualizar'}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Global totals */}
            {data && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <KpiTile label="Ingresos totales" value={fmt(data.totals.revenue)} sub={`${fmtInt(data.totals.sales_count)} ventas`} tone="dark" icon={Money} testId="kpi-total-revenue" />
                    <KpiTile label="Certificados" value={fmtInt(data.totals.certificates_count)} icon={TrendUp} />
                    <KpiTile label="Ubicaciones con venta" value={data.totals.active_locations} sub={`${data.totals.inactive_locations} sin actividad`} icon={Storefront} />
                    <KpiTile label="Periodo" value={`${data.period.days} días`} sub={`${data.period.start_date} → ${data.period.end_date}`} icon={CalendarBlank} />
                </div>
            )}

            {/* Inactive locations alert */}
            {data && data.inactive_locations.length > 0 && (
                <InsightCard
                    tone="bad" icon={Warning}
                    title={`${data.inactive_locations.length} ubicación(es) sin actividad en el periodo`}
                    body={`${data.inactive_locations.map(l => l.name).join(', ')}. Considera reasignar staff, suspender operación o renegociar el contrato.`}
                    testId="inactive-locations-alert"
                />
            )}

            {/* Per-location details */}
            {data && data.locations.length === 0 && (
                <Card className="border-[#ECE3D2]">
                    <CardContent className="py-12 text-center text-sm text-[#8B8474]">
                        <Lightbulb size={28} weight="duotone" className="mx-auto mb-2 text-[#ECE3D2]" />
                        Sin ventas en el periodo y rango seleccionado. Ajusta las fechas.
                    </CardContent>
                </Card>
            )}

            <div className="space-y-5">
                {data?.locations.map(loc => (
                    <LocationDetail key={loc.location.id} loc={loc} />
                ))}
            </div>
        </div>
    );
};

export default ProductivityPage;
